import React, { useState, useEffect } from 'react';
import { validaDocumento, formaCodice } from '../utils/codiceFiscale';
import '../styles/CampoDocumento.css';

/**
 * CampoDocumento — codice fiscale, con le alternative nascoste.
 *
 * I pazienti senza codice fiscale italiano sono il due o tre per
 * cento: le alternative stanno dietro un collegamento, cosi' il resto
 * dei casi lavora esattamente come prima.
 *
 * Se qualcuno scrive solo cifre, l'errore non dice "formato non
 * valido" ma indica la strada: chi ha un codice ISS non sa di dover
 * cercare un'opzione nascosta.
 *
 * Props:
 *  - valore, tipo          codice e tipo_documento
 *  - onChange              (codice) => void
 *  - onTipoChange          (tipo) => void
 *  - anagrafica            { data_nascita, sesso } per la coerenza
 *  - grande                comandi piu' larghi, per il tablet
 *  - errore                messaggio dall'esterno
 */

const TIPI = [
    ['cf_italiano', 'Codice fiscale italiano', 'Codice fiscale'],
    ['iss_san_marino', 'Codice ISS San Marino', 'Codice ISS'],
    ['estero', 'Altro documento estero', 'Documento identificativo'],
    /* STP (straniero temporaneamente presente) ed ENI (europeo non
       iscritto), dal 3/10/2026: sedici caratteri che non sono un codice
       fiscale. Prima finivano fra i documenti esteri senza nessun controllo,
       o nel campo del codice fiscale a dire «formato non valido». */
    ['stp_eni', 'Codice STP o ENI', 'Codice STP o ENI'],
];

const CampoDocumento = ({
    valore, tipo = 'cf_italiano', onChange, onTipoChange,
    anagrafica = {}, grande, errore,
}) => {
    const [alternativeAperte, setAlternativeAperte] = useState(tipo !== 'cf_italiano');
    const [esito, setEsito] = useState(null);

    const etichetta = (TIPI.find(([t]) => t === tipo) || TIPI[0])[2];

    useEffect(() => {
        if (!valore) { setEsito(null); return; }
        setEsito(validaDocumento(valore, tipo, anagrafica));
    }, [valore, tipo, anagrafica.data_nascita, anagrafica.sesso]);

    /* Sole cifre in un campo che ne aspetta sedici: quasi certamente
       un codice ISS. L'errore lo dice invece di lasciare in mezzo. */
    const forseIss = tipo === 'cf_italiano'
        && valore
        && /^\d{1,7}$/.test(String(valore).trim());

    /* Un codice STP o ENI scritto nel campo del codice fiscale: come per
       l'ISS, l'errore indica la strada invece di lasciare in mezzo. Decide la
       forma intera (tre lettere e tredici cifre), non il prefisso: STP sono
       anche le consonanti di un cognome. */
    const forseStpEni = tipo === 'cf_italiano'
        && valore
        && ['stp', 'eni'].includes(formaCodice(valore));

    const messaggio = errore
        || (esito && !esito.valido && valore ? esito.errore : null);

    return (
        <div className={`cdoc ${grande ? 'cdoc--grande' : ''}`}>
            <span className="cdoc-etichetta">{etichetta}</span>

            <input
                className={messaggio ? 'cdoc-input errore' : 'cdoc-input'}
                value={valore || ''}
                maxLength={tipo === 'cf_italiano' || tipo === 'stp_eni' ? 16 : (tipo === 'iss_san_marino' ? 7 : 30)}
                inputMode={tipo === 'iss_san_marino' ? 'numeric' : 'text'}
                onChange={(e) => {
                    const v = tipo === 'iss_san_marino'
                        ? e.target.value.replace(/\D/g, '').slice(0, 7)
                        : e.target.value.toUpperCase().replace(/\s/g, '');
                    onChange(v);
                }}
                autoComplete="off"
            />

            {forseIss && (
                <button
                    type="button"
                    className="cdoc-suggerimento"
                    onClick={() => {
                        onTipoChange('iss_san_marino');
                        setAlternativeAperte(true);
                    }}
                >
                    Se è un codice ISS di San Marino, tocca qui
                </button>
            )}

            {forseStpEni && (
                <button
                    type="button"
                    className="cdoc-suggerimento"
                    onClick={() => {
                        onTipoChange('stp_eni');
                        setAlternativeAperte(true);
                    }}
                >
                    È un codice STP o ENI: tocca qui
                </button>
            )}

            {messaggio && !forseIss && !forseStpEni && (
                <span className="cdoc-errore">{messaggio}</span>
            )}

            {esito && esito.valido && esito.avvisi.length > 0 && (
                <span className="cdoc-avviso">{esito.avvisi[0]}</span>
            )}

            {!alternativeAperte ? (
                <button
                    type="button"
                    className="cdoc-collegamento"
                    onClick={() => setAlternativeAperte(true)}
                >
                    Il paziente non ha un codice fiscale italiano
                </button>
            ) : (
                <div className="cdoc-tipi">
                    {TIPI.map(([t, nome]) => (
                        <button
                            key={t}
                            type="button"
                            className={tipo === t ? 'scelto' : ''}
                            onClick={() => {
                                onTipoChange(t);
                                // Il codice precedente non vale piu': un
                                // ISS in un campo da codice fiscale
                                // resterebbe li' a sembrare valido.
                                if (t !== tipo) onChange('');
                            }}
                        >
                            {nome}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CampoDocumento;
